import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    toastMessage: String,
    toastLinkLabel: String,
    toastLinkUrl: String
  }

  launch(event) {
    const form = event.target.closest("form")
    if (!form || !["save", "unsave"].includes(form.dataset.tryThisAction)) return

    const btn = form.querySelector("button, input[type=submit]")
    const btnRect = btn ? btn.getBoundingClientRect() : this.element.getBoundingClientRect()
    const startX = btnRect.left + btnRect.width / 2
    const startY = btnRect.top + btnRect.height / 2

    const navIcon = document.querySelector("[data-navbar-celebrate-target='icon']")
    const navIconRect = navIcon ? navIcon.getBoundingClientRect() : null

    navigator.vibrate?.(50)
    this._pop(btn)

    if (form.dataset.tryThisAction === "save") {
      this._animateSave(btn)
      this._showToast()
      window.dispatchEvent(new CustomEvent("frankly:try-saved"))
    }

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (
      form.dataset.tryThisAction !== "save" ||
      reducedMotion ||
      !navIconRect ||
      !CSS.supports("offset-path", 'path("M 0 0 L 1 1")')
    ) return

    const endX = navIconRect.left + navIconRect.width / 2
    const endY = navIconRect.top + navIconRect.height / 2
    const midX = (startX + endX) / 2
    const midY = Math.min(startY, endY) - 60

    this._launchPlane(startX, startY, midX, midY, endX, endY)
  }

  _pop(btn) {
    if (!btn || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    btn.classList.remove("try-fx-pop")
    void btn.offsetWidth
    btn.classList.add("try-fx-pop")
    setTimeout(() => btn.classList.remove("try-fx-pop"), 580)
  }

  _animateSave(btn) {
    if (!btn || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    btn.classList.remove("try-fx-save-fill")
    void btn.offsetWidth
    btn.classList.add("try-fx-save-fill")

    // Strip the fill before Turbo caches the page so a restored snapshot
    // doesn't resurrect the frozen `forwards` animation state.
    document.addEventListener(
      "turbo:before-cache",
      () => btn.classList.remove("try-fx-save-fill"),
      { once: true }
    )

    const iconPath = btn.querySelector("[data-try-save-fx-target='iconPath']")
    if (iconPath) {
      setTimeout(() => {
        iconPath.setAttribute("d", "M5 12.5 9.5 17 19 7")
      }, 220)
    }
  }

  _launchPlane(startX, startY, midX, midY, endX, endY) {
    const plane = document.createElement("div")
    plane.className = "try-fx-plane"
    plane.dataset.turboTemporary = ""
    plane.style.offsetPath = `path("M ${startX} ${startY} Q ${midX} ${midY} ${endX} ${endY}")`
    plane.style.left = "0"
    plane.style.top = "0"
    plane.innerHTML = this._planeSvg()
    document.body.appendChild(plane)

    setTimeout(() => {
      plane.remove()
      this._burst(endX, endY)
    }, 680)
  }

  _burst(cx, cy) {
    const ring = document.createElement("div")
    ring.className = "try-fx-burst"
    ring.dataset.turboTemporary = ""
    ring.style.left = `${cx}px`
    ring.style.top = `${cy}px`
    document.body.appendChild(ring)

    const offsets = [[-16, -14], [14, -18], [20, 8], [-8, 18], [-20, 6]]
    offsets.forEach(([tx, ty], i) => {
      const dot = document.createElement("div")
      dot.className = "try-fx-sparkle"
      dot.dataset.turboTemporary = ""
      dot.style.left = `${cx}px`
      dot.style.top = `${cy}px`
      dot.style.setProperty("--tx", `${tx}px`)
      dot.style.setProperty("--ty", `${ty}px`)
      dot.style.animationDelay = `${i * 0.04}s`
      dot.style.backgroundColor = i % 2 === 0
        ? "rgb(var(--fk-bluegreen-400))"
        : "rgb(var(--fk-orange-400))"
      document.body.appendChild(dot)

      setTimeout(() => dot.remove(), 520 + i * 40)
    })

    setTimeout(() => ring.remove(), 520)
  }

  _showToast() {
    const toast = document.createElement("div")
    toast.className = "try-fx-toast"
    toast.dataset.turboTemporary = ""

    const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    icon.setAttribute("width", "16")
    icon.setAttribute("height", "16")
    icon.setAttribute("viewBox", "0 0 24 24")
    icon.setAttribute("fill", "none")
    icon.setAttribute("stroke", "white")
    icon.setAttribute("stroke-width", "3")
    icon.setAttribute("stroke-linecap", "round")
    icon.setAttribute("stroke-linejoin", "round")
    icon.setAttribute("aria-hidden", "true")
    const checkmark = document.createElementNS("http://www.w3.org/2000/svg", "polyline")
    checkmark.setAttribute("points", "20 6 9 17 4 12")
    icon.appendChild(checkmark)

    const msg = document.createElement("span")
    msg.textContent = this.toastMessageValue
    toast.appendChild(icon)
    toast.appendChild(msg)

    if (this.hasToastLinkLabelValue && this.hasToastLinkUrlValue) {
      const link = document.createElement("a")
      link.href = this.toastLinkUrlValue
      link.textContent = this.toastLinkLabelValue
      link.className = "try-fx-toast__link"
      toast.appendChild(link)
    }

    document.body.appendChild(toast)

    setTimeout(() => {
      toast.classList.add("try-fx-toast--out")
      setTimeout(() => toast.remove(), 220)
    }, 2500)
  }

  _planeSvg() {
    return `<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" width="32" height="32">
      <polygon points="2,16 30,4 22,28" fill="#67bcb7" stroke="white" stroke-width="1.2"/>
      <line x1="2" y1="16" x2="22" y2="16" stroke="white" stroke-width="1.2" opacity="0.6"/>
      <polygon points="2,16 22,16 22,21" fill="#c7812a" opacity="0.85"/>
      <circle cx="30" cy="4" r="1.5" fill="white"/>
    </svg>`
  }
}
